import {
	Box,
	Button,
	Chip,
	CircularProgress,
	Divider,
	MenuItem,
	Paper,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FaChevronDown, FaChevronUp, FaSyncAlt } from 'react-icons/fa';
import { useNotify } from 'react-admin';
import constants from '../../constants';
import { httpClient } from '../../providers/myDataProvider';

// GH-778 — "אתגר חבר" tracking inside the step-challenge admin page (read-only, per company).
// KPI cards come from GET step-challenge/admin/friend-invites/kpis; the invites table is loaded only on
// demand (GET step-challenge/admin/friend-invites, paginated). Invitees who are not our members appear
// with a MASKED phone only — the UI never shows a full number, even if one slipped into the response.

type FriendKpis = {
	invites_sent: number;
	inviters: number;
	accepted: number;
	declined: number;
	expired: number;
	registered_via_invite: number;
	joined_via_invite: number;
	duels_active: number;
	duels_completed: number;
	invite_rate: number | null; // inviters / joined participants of the cycle (0..1)
	accept_rate: number | null; // accepted / sent (0..1)
	invitee_avg_daily: number | null;
	others_avg_daily: number | null;
};

type FriendInviteRow = {
	_id?: string;
	created_at: string;
	inviter_first_name: string | null;
	invitee_first_name: string | null;
	invitee_phone_masked: string | null;
	status: string;
	placement?: string | null;
	duel: { result: string | null; a_avg: number | null; b_avg: number | null } | null;
};

type CycleLite = { _id: string; start_date: string; end_date: string; status: string };

const PURPLE = '#6D28D9';
const heDate = (iso?: string | null) => (iso ? iso.split('-').reverse().join('.') : '—');
const num = (n?: number | null) => (n == null ? '—' : Math.round(n).toLocaleString());
const pct = (n?: number | null) => (n == null ? '—' : `${Math.round(n * 100)}%`);
// Defence in depth: whatever arrives in the masked field, render at most the last 4 digits.
const maskPhone = (s?: string | null) => {
	if (!s) return null;
	const digits = s.replace(/\D/g, '');
	return digits.length > 4 ? `••••${digits.slice(-4)}` : s;
};

const STATUS_LABELS: Record<string, { text: string; color: 'success' | 'warning' | 'default' | 'error' }> = {
	pending: { text: 'ממתינה', color: 'warning' },
	accepted: { text: 'אושרה', color: 'success' },
	declined: { text: 'נדחתה', color: 'error' },
	expired: { text: 'פג תוקף', color: 'default' },
};
const PLACEMENT_LABELS: Record<string, string> = {
	joint: 'נבחרת משותפת',
	next_cycle: 'במחזור הבא',
	none: '—',
};
// Duel result as seen from the inviter (a) vs the invitee (b).
const duelResultLabel = (result?: string | null) => {
	if (!result || result === 'active') return 'פעיל';
	if (['a', 'inviter', 'won', 'a_won'].includes(result)) return 'המזמין/ה ניצח/ה';
	if (['b', 'invitee', 'lost', 'b_won'].includes(result)) return 'המוזמן/ת ניצח/ה';
	if (result === 'tie') return 'תיקו';
	if (result === 'cancelled') return 'בוטל';
	return result;
};

const KpiCard = ({ label, value, sub }: { label: string; value: string; sub?: string }) => (
	<Paper sx={{ p: 2, flex: 1, minWidth: 150, textAlign: 'center', borderRadius: 3 }}>
		<Typography variant="h4" sx={{ fontWeight: 700, color: PURPLE }}>
			{value}
		</Typography>
		<Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
			{label}
		</Typography>
		{sub ? (
			<Typography variant="caption" sx={{ color: 'text.disabled' }}>
				{sub}
			</Typography>
		) : null}
	</Paper>
);

// httpClient is axios → errors carry response.status / response.data.message.
const errMsg = (e: any) =>
	e?.response?.status === 403
		? 'אין הרשאה לנתוני אתגר חבר'
		: e?.response?.data?.message || e?.body?.message || e?.message || 'שגיאה בטעינה';

export default function FriendInvitesSection({
	companyId,
	cycles,
	enabled,
}: {
	companyId: string;
	cycles: CycleLite[];
	enabled?: boolean;
}) {
	const notify = useNotify();
	// Open by default only when the overview says the company flag is on; otherwise nothing is fetched until opened.
	const [open, setOpen] = useState<boolean>(enabled === true);
	const [cycle, setCycle] = useState<string>(cycles[0]?._id ?? ''); // '' = all cycles
	const [kpis, setKpis] = useState<FriendKpis | null>(null);
	const [kpisLoading, setKpisLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [showTable, setShowTable] = useState(false);
	const [rows, setRows] = useState<FriendInviteRow[]>([]);
	const [total, setTotal] = useState(0);
	const [page, setPage] = useState(0); // 0-based for TablePagination
	const [limit, setLimit] = useState(25);
	const [rowsLoading, setRowsLoading] = useState(false);
	const kpiReq = useRef(0);
	const rowsReq = useRef(0);

	const baseParams = useCallback(() => {
		const params = new URLSearchParams({ company: companyId });
		if (cycle) params.set('cycle', cycle);
		return params;
	}, [companyId, cycle]);

	const loadKpis = useCallback(async () => {
		const req = ++kpiReq.current;
		setKpisLoading(true);
		setError(null);
		try {
			const res = await httpClient(`${constants.API_URL}step-challenge/admin/friend-invites/kpis?${baseParams().toString()}`, {
				method: 'GET',
			});
			if (req === kpiReq.current) setKpis(res.json as FriendKpis);
		} catch (e: any) {
			if (req === kpiReq.current) {
				setKpis(null);
				setError(errMsg(e));
				notify(errMsg(e), { type: 'error' });
			}
		} finally {
			if (req === kpiReq.current) setKpisLoading(false);
		}
	}, [baseParams, notify]);

	const loadRows = useCallback(async () => {
		const req = ++rowsReq.current;
		setRowsLoading(true);
		try {
			const params = baseParams();
			params.set('page', String(page + 1));
			params.set('limit', String(limit));
			const res = await httpClient(`${constants.API_URL}step-challenge/admin/friend-invites?${params.toString()}`, {
				method: 'GET',
			});
			if (req !== rowsReq.current) return;
			const json: any = res.json;
			const list: FriendInviteRow[] = Array.isArray(json) ? json : json?.data ?? json?.rows ?? json?.items ?? [];
			setRows(list);
			setTotal(typeof json?.total === 'number' ? json.total : list.length);
		} catch (e: any) {
			if (req === rowsReq.current) {
				setRows([]);
				setTotal(0);
				notify(errMsg(e), { type: 'error' });
			}
		} finally {
			if (req === rowsReq.current) setRowsLoading(false);
		}
	}, [baseParams, page, limit, notify]);

	useEffect(() => {
		if (open) loadKpis();
	}, [open, loadKpis]);

	useEffect(() => {
		if (open && showTable) loadRows();
	}, [open, showTable, loadRows]);

	const cycleLabel = (c: CycleLite) => `${heDate(c.start_date)} – ${heDate(c.end_date)}${c.status === 'active' ? ' (פעיל)' : ''}`;

	return (
		<Box mt={2}>
			<Divider sx={{ mb: 1.5 }} />
			<Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
				<Stack direction="row" spacing={1} alignItems="center">
					<Button size="small" onClick={() => setOpen((o) => !o)} startIcon={open ? <FaChevronUp /> : <FaChevronDown />}>
						<Typography variant="subtitle1" component="span" sx={{ fontWeight: 600 }}>
							אתגר חבר (GH-778)
						</Typography>
					</Button>
					{enabled === false ? (
						<Tooltip title='הדגל "אתגר חבר" כבוי בדף החברה — העובדים לא רואים את הכפתור'>
							<Chip size="small" label="כבוי לחברה" />
						</Tooltip>
					) : null}
				</Stack>
				{open ? (
					<Stack direction="row" spacing={1} alignItems="center">
						<TextField
							select
							size="small"
							label="מחזור"
							value={cycle}
							InputLabelProps={{ shrink: true }}
							SelectProps={{ displayEmpty: true }}
							onChange={(e) => {
								setCycle(e.target.value);
								setPage(0);
							}}
							sx={{ minWidth: 220 }}
						>
							<MenuItem value="">כל המחזורים</MenuItem>
							{cycles.map((c) => (
								<MenuItem key={c._id} value={c._id}>
									{cycleLabel(c)}
								</MenuItem>
							))}
						</TextField>
						<Button
							size="small"
							variant="outlined"
							startIcon={<FaSyncAlt />}
							disabled={kpisLoading || rowsLoading}
							onClick={() => {
								loadKpis();
								if (showTable) loadRows();
							}}
						>
							רענון
						</Button>
					</Stack>
				) : null}
			</Stack>

			{open ? (
				<Box mt={1.5}>
					{kpisLoading && !kpis ? (
						<CircularProgress size={20} />
					) : error && !kpis ? (
						<Typography variant="body2" color="error">
							{error}
						</Typography>
					) : kpis ? (
						<Stack direction="row" sx={{ flexWrap: 'wrap', gap: 2, opacity: kpisLoading ? 0.5 : 1 }}>
							<KpiCard label="הזמנות שנשלחו" value={num(kpis.invites_sent)} sub={`${num(kpis.inviters)} מזמינים/ות`} />
							<KpiCard label="שיעור מזמינים" value={pct(kpis.invite_rate)} sub="מזמינים / מצטרפים למחזור" />
							<KpiCard
								label="שיעור אישור"
								value={pct(kpis.accept_rate)}
								sub={`${num(kpis.accepted)} אישרו · ${num(kpis.declined)} דחו · ${num(kpis.expired)} פגו`}
							/>
							<KpiCard
								label="הצטרפו דרך הזמנה"
								value={num(kpis.joined_via_invite)}
								sub={`${num(kpis.registered_via_invite)} נרשמו לאפליקציה דרך הזמנה`}
							/>
							<KpiCard label="דו-קרבים פעילים" value={num(kpis.duels_active)} sub={`${num(kpis.duels_completed)} הסתיימו`} />
							<KpiCard
								label="ממוצע צעדים יומי — מוזמנים"
								value={num(kpis.invitee_avg_daily)}
								sub={`שאר המשתתפים: ${num(kpis.others_avg_daily)}`}
							/>
						</Stack>
					) : null}

					<Box mt={2}>
						<Button size="small" variant="text" onClick={() => setShowTable((s) => !s)}>
							{showTable ? 'הסתר הזמנות' : 'הצג הזמנות'}
						</Button>
					</Box>

					{showTable ? (
						<TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
							{rowsLoading ? (
								<Stack alignItems="center" p={3}>
									<CircularProgress size={24} />
								</Stack>
							) : (
								<Table size="small">
									<TableHead>
										<TableRow>
											<TableCell align="right">נשלחה</TableCell>
											<TableCell align="right">מזמין/ה</TableCell>
											<TableCell align="right">מוזמן/ת</TableCell>
											<TableCell align="right">טלפון</TableCell>
											<TableCell align="right">סטטוס</TableCell>
											<TableCell align="right">שיבוץ</TableCell>
											<TableCell align="right">דו-קרב (ממוצע יומי מזמין : מוזמן)</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{rows.map((r, i) => {
											const st = STATUS_LABELS[r.status];
											const phone = maskPhone(r.invitee_phone_masked);
											return (
												<TableRow key={r._id || `${page}-${i}`}>
													<TableCell align="right">
														{r.created_at ? new Date(r.created_at).toLocaleDateString('he-IL') : '—'}
													</TableCell>
													<TableCell align="right">{r.inviter_first_name || '—'}</TableCell>
													<TableCell align="right">
														{r.invitee_first_name || (
															<Chip size="small" variant="outlined" label="לא רשום/ה" />
														)}
													</TableCell>
													<TableCell align="right">
														{phone ? (
															<Typography variant="body2" component="span" dir="ltr">
																{phone}
															</Typography>
														) : (
															'—'
														)}
													</TableCell>
													<TableCell align="right">
														<Chip size="small" label={st?.text || r.status} color={st?.color || 'default'} />
													</TableCell>
													<TableCell align="right">{r.placement ? PLACEMENT_LABELS[r.placement] ?? r.placement : '—'}</TableCell>
													<TableCell align="right">
														{r.duel ? (
															<>
																{num(r.duel.a_avg)} : {num(r.duel.b_avg)}{' '}
																<Chip size="small" label={duelResultLabel(r.duel.result)} sx={{ ml: 1 }} />
															</>
														) : (
															'—'
														)}
													</TableCell>
												</TableRow>
											);
										})}
										{!rows.length ? (
											<TableRow>
												<TableCell colSpan={7} align="right">
													<Typography variant="body2" color="text.secondary">
														אין הזמנות.
													</Typography>
												</TableCell>
											</TableRow>
										) : null}
									</TableBody>
								</Table>
							)}
							<TablePagination
								component="div"
								count={total}
								page={page}
								onPageChange={(_, p) => setPage(p)}
								rowsPerPage={limit}
								onRowsPerPageChange={(e) => {
									setLimit(parseInt(e.target.value, 10));
									setPage(0);
								}}
								rowsPerPageOptions={[10, 25, 50, 100]}
								labelRowsPerPage="שורות בעמוד"
							/>
						</TableContainer>
					) : null}
				</Box>
			) : null}
		</Box>
	);
}
